import { Card, Col, Flex, Form, Row, Space, Spin, Typography } from 'antd';
import React, { useEffect, useState } from 'react';
import IntlMessage from '../../components/IntlMessage';
import dayjs from 'dayjs';
import reportApi from '../../api/report';
import { Line } from '@ant-design/plots';
import {
  formatCurrency,
  formatNumber,
  formatShortNumber,
} from '../../helpers/helpers';
import { chartType } from '../../utils/constant';
import { useQueryClient } from '@tanstack/react-query';
import 'dayjs/locale/fr';

dayjs.locale('fr');

const { Text } = Typography;

const fs_16 = {
  fontSize: '16px',
};

const Revenue = () => {
  const [filter, setFilter] = useState({
    mode: 'date',
    from_date: dayjs().startOf('month').toISOString(),
    to_date: dayjs().endOf('month').toISOString(),
    type: chartType.REVENUE,
  });
  const [loading, setLoading] = useState(false);
  const [loadingChart, setLoadingChart] = useState(false);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState({
    day: {
      revenue: 0,
      total_seat: 0,
      total_count: 0,
      filling_rate: 0,
    },
    month: {
      revenue: 0,
      total_seat: 0,
      total_count: 0,
      filling_rate: 0,
    },
  });
  const [type, setType] = useState('revenue');
  const queryClient = useQueryClient();
  useEffect(() => {
    async function fetchData() {
      queryClient.invalidateQueries({ queryKey: ['account'] });
      try {
        setLoading(true);

        const data = await Promise.all([
          reportApi.getRevenue(filter),
          reportApi.getRevenueTotal(filter),
        ]);

        setItems(data[0].data);
        // setDataPassengers(data[0]?.data_passengers);
        setTotal(data[1]);
        setLoading(false);
      } catch (error) {
        loading(false);
        console.error(error);
      }
    }
    fetchData();
  }, []);

  const option = {
    data: items,
    xField: 'date',
    yField: 'total',
    seriesField: 'title',
    legend: {
      color: {
        layout: {
          justifyContent: 'center',
          alignItems: 'center',
          flexDirection: 'column',
        },
      },
    },
    stack: false,
    animation: {
      appear: {
        animation: 'path-in',
        duration: 1000,
      },
    },
    scale: {
      color: {
        range: ['#2688FF', 'red'],
      },
    },
    shapeField: 'smooth',
    point: {
      shapeField: 'square',
      sizeField: 1,
    },
    style: {
      lineWidth: 2.5,
      stroke: '#3c74ce',
      strokeOpacity: 1,
      lineDash: [0, 0],
    },

    axis: {
      y: {
        position: 'right',
        labelFormatter: (value) => {
          return formatShortNumber(Number(value));
        },
        gridLineWidth: 2,
        gridStroke: '#3c74ce',
        gridLineDash: [0, 0],
      },
      x: {
        labelFormatter: (value) => {
          return dayjs(value, 'YYYY/MM/DD').format('DD MMM YY');
        },
        labelAutoRotate: false,
        labelAutoHide: true,
      },
    },
    interaction: {
      tooltip: {
        render: (e, { title, items }) => {
          const list = items;
          const item = items.find((item) => item.name === 'date');

          return (
            <div key={title}>
              <div>{dayjs(title, 'YYYY/MM/DD').format('DD MMM YY')}</div>
              {list
                .filter((item) => item.name !== 'date' && item.name !== 'total')
                .map((item) => {
                  const { value, name, color } = item;
                  return (
                    <div>
                      <div
                        style={{
                          margin: 0,
                          display: 'flex',
                          justifyContent: 'space-between',
                          minWidth: 150,
                        }}>
                        <div>
                          <span
                            style={{
                              display: 'inline-block',
                              width: 6,
                              height: 6,
                              borderRadius: '50%',
                              backgroundColor: color,
                              marginRight: 6,
                            }}></span>
                          <span>
                            {name === 'Revenue'
                              ? 'Revenus '
                              : name === 'Filling rate'
                                ? 'Taux de remplissage '
                                : name === 'Trips'
                                  ? 'Voyages'
                                  : 'Passagers '}
                          </span>
                        </div>
                        <b>
                          {name === 'Revenue'
                            ? formatCurrency(value)
                            : name === 'Filling rate'
                              ? '  ' + value + ' %'
                              : formatNumber(value)}
                        </b>
                      </div>
                    </div>
                  );
                })}
            </div>
          );
        },
      },
    },
  };

  const onChangeType = async (check) => {
    try {
      setLoadingChart(true);
      setType(check);
      setFilter((value) => {
        return {
          ...value,
          type: check,
        };
      });
      const payload = {
        ...filter,
        type: check,
      };
      const data = await Promise.all([reportApi.getRevenue(payload)]);

      setItems(data[0].data);

      setLoadingChart(false);
    } catch (error) {
      console.error(error);
      setLoadingChart(false);
    }
  };

  return (
    <Row style={{ minHeight: '100%' }} gutter={[16, 16]}>
      <Col span={24}>
        <Card bordered>
          <Row className="page-title" justify={'space-between'}>
            <Col span={24}>
              {/* <PageTitle title={<IntlMessage id="global.dashboard" />} /> */}
              <Text style={{ fontSize: '20px' }} strong>
                <IntlMessage id="global.dashboard" />
              </Text>
            </Col>
          </Row>

          <Row gutter={[8, 8]} justify={'space-around'} wrap>
            <Col
              xs={{ span: 12 }}
              sm={{ span: 12 }}
              md={{ span: 6 }}
              lg={{ span: 6 }}>
              <Card
                bordered
                style={
                  type === chartType.REVENUE
                    ? {
                        backgroundColor: '#15bc8c0f',
                        boxShadow: '#15bc8c 0px 0px 5px',
                        border: '1px solid #15bc8c',
                        height: '100%',
                      }
                    : {
                        height: '100%',
                      }
                }
                hoverable={true}
                onClick={() => onChangeType(chartType.REVENUE)}>
                <Spin spinning={loading}>
                  <Space
                    direction="vertical"
                    style={{ minWidth: '75px', width: '100%' }}>
                    <Text style={{ fontWeight: 'bold' }}>
                      <IntlMessage id="global.revenue" /> (CFA)
                    </Text>
                    <Flex justify="space-between" wrap>
                      <Text>Aujourd'hui </Text>
                      <Text strong style={fs_16}>
                        {formatNumber(total.day.revenue)}
                      </Text>
                    </Flex>
                    <Flex justify="space-between" wrap>
                      <Text>{dayjs().format('MMMM')} </Text>
                      <Text strong style={fs_16}>
                        {formatNumber(total.month.revenue)}
                      </Text>
                    </Flex>
                  </Space>
                </Spin>
              </Card>
            </Col>
            <Col
              xs={{ span: 12 }}
              sm={{ span: 12 }}
              md={{ span: 6 }}
              lg={{ span: 6 }}>
              <Card
                bordered
                style={
                  type === chartType.PASSENGERS
                    ? {
                        backgroundColor: '#15bc8c0f',
                        boxShadow: '#15bc8c 0px 0px 5px',
                        border: '1px solid #15bc8c',
                        height: '100%',
                      }
                    : {
                        height: '100%',
                      }
                }
                hoverable={true}
                onClick={() => onChangeType(chartType.PASSENGERS)}>
                <Spin spinning={loading}>
                  <Space
                    direction="vertical"
                    style={{ minWidth: '75px', width: '100%' }}>
                    <Text style={{ fontWeight: 'bold' }}>
                      <IntlMessage id="global.passengers" />
                    </Text>
                    <Flex justify="space-between" wrap>
                      <Text>Aujourd'hui </Text>
                      <Text strong style={fs_16}>
                        {' '}
                        {formatNumber(total.day.total_seat)}
                      </Text>
                    </Flex>
                    <Flex justify="space-between" wrap>
                      <Text>{dayjs().format('MMMM')}</Text>
                      <Text strong style={fs_16}>
                        {' '}
                        {formatNumber(total.month.total_seat)}
                      </Text>
                    </Flex>
                  </Space>
                </Spin>
              </Card>
            </Col>
            <Col
              xs={{ span: 12 }}
              sm={{ span: 12 }}
              md={{ span: 6 }}
              lg={{ span: 6 }}>
              <Card
                bordered
                style={
                  type === chartType.FILLING_RATE
                    ? {
                        backgroundColor: '#15bc8c0f',
                        boxShadow: '#15bc8c 0px 0px 5px',
                        border: '1px solid #15bc8c',
                        height: '100%',
                      }
                    : {
                        height: '100%',
                      }
                }
                hoverable={true}
                onClick={() => onChangeType(chartType.FILLING_RATE)}>
                <Spin spinning={loading}>
                  <Space
                    direction="vertical"
                    style={{ minWidth: '75px', width: '100%' }}>
                    <Text style={{ fontWeight: 'bold' }}>
                      <IntlMessage id="global.filling_rate" />
                    </Text>
                    <Flex justify="space-between" wrap>
                      <Text>Aujourd'hui </Text>
                      <Text strong style={fs_16}>
                        {' '}
                        {`${Number(total.day.filling_rate)} %`}
                      </Text>
                    </Flex>
                    <Flex justify="space-between" wrap>
                      <Text>{dayjs().format('MMMM')}</Text>
                      <Text strong style={fs_16}>{`${Number(
                        total.month.filling_rate,
                      )} %`}</Text>
                    </Flex>
                  </Space>
                </Spin>
              </Card>
            </Col>
            <Col
              xs={{ span: 12 }}
              sm={{ span: 12 }}
              md={{ span: 6 }}
              lg={{ span: 6 }}>
              <Card
                bordered
                style={
                  type === chartType.TRIPS
                    ? {
                        backgroundColor: '#15bc8c0f',
                        boxShadow: '#15bc8c 0px 0px 5px',
                        border: '1px solid #15bc8c',
                        height: '100%',
                      }
                    : {
                        height: '100%',
                      }
                }
                hoverable={true}
                onClick={() => onChangeType(chartType.TRIPS)}>
                <Spin spinning={loading}>
                  <Space
                    direction="vertical"
                    style={{ minWidth: '75px', width: '100%' }}>
                    <Text style={{ fontWeight: 'bold' }}>
                      <IntlMessage id="global.trips" />
                    </Text>
                    <Flex justify="space-between" wrap>
                      <Text>Aujourd'hui </Text>
                      <Text strong style={fs_16}>
                        {formatNumber(total.day.total_count)}
                      </Text>
                    </Flex>
                    <Flex justify="space-between" wrap>
                      <Text>{dayjs().format('MMMM')} </Text>
                      <Text strong style={fs_16}>
                        {formatNumber(total.month.total_count)}
                      </Text>
                    </Flex>
                  </Space>
                </Spin>
              </Card>
            </Col>
          </Row>

          <Row gutter={16} justify={'space-around'} wrap>
            <Col span={24}>
              <br />
              {loading || loadingChart ? (
                <Spin spinning={loading || loadingChart}></Spin>
              ) : (
                <div style={{ height: '400px' }}>
                  <Line {...option} />
                </div>
              )}
            </Col>
          </Row>
        </Card>
      </Col>
      <br />
    </Row>
  );
};

export default Revenue;
